import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, FileText, Check, X as RejectIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't show popup on legal pages
    const legalPages = ['/privacy-policy', '/cookie-policy', '/terms-of-service'];
    const isLegalPage = legalPages.includes(location.pathname);
    
    if (isLegalPage) {
      setIsVisible(false);
      return;
    }

    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setIsVisible(false);
  };

  const handleClose = () => {
    setIsVisible(false);
    // Don't set any preference, user can see it again on next visit
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={handleClose}></div>
      
      {/* Cookie Consent Popup */}
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Cookie size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Cookie Notice</h3>
                  <p className="text-blue-100 text-sm">We use cookies to enhance your experience</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!showDetails ? (
              <>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  We use cookies to improve your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies.
                </p>
                
                {/* Quick Actions */}
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={handleAccept}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Check size={16} />
                    Accept All
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <RejectIcon size={16} />
                    Reject
                  </button>
                </div>

                {/* More Options */}
                <div className="flex items-center justify-between text-sm">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Customize Settings
                  </button>
                  <div className="flex gap-3">
                    <Link 
                      to="/privacy-policy" 
                      className="text-gray-600 hover:text-blue-600 flex items-center gap-1 text-xs"
                      onClick={handleClose}
                    >
                      <Shield size={12} />
                      Privacy
                    </Link>
                    <Link 
                      to="/cookie-policy" 
                      className="text-gray-600 hover:text-blue-600 flex items-center gap-1 text-xs"
                      onClick={handleClose}
                    >
                      <FileText size={12} />
                      Cookies
                    </Link>
                    <Link 
                      to="/terms-of-service" 
                      className="text-gray-600 hover:text-blue-600 flex items-center gap-1 text-xs"
                      onClick={handleClose}
                    >
                      <FileText size={12} />
                      Terms
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h4 className="font-bold text-gray-900 mb-4">Cookie Preferences</h4>
                
                {/* Essential Cookies */}
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-blue-800">Essential Cookies</h5>
                    <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">Always Active</div>
                  </div>
                  <p className="text-blue-700 text-sm">
                    These cookies are necessary for the website to function and cannot be switched off.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="mb-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-indigo-800">Analytics Cookies</h5>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  <p className="text-indigo-700 text-sm">
                    These cookies help us understand how visitors interact with our website.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="mb-6 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-cyan-800">Marketing Cookies</h5>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>
                  <p className="text-cyan-700 text-sm">
                    These cookies are used to deliver advertisements more relevant to you.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleAccept}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                  >
                    Save Preferences
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
